import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { updateWorkItemPreset } from '../../actions/apiWorkItemPresets';
import HelpTooltip from '../../components/HelpTooltip';
import PresetDefaultParts from '../../components/Parts/PresetDefaultParts';
import _ from 'lodash';

const INPUT_TYPES = ['text', 'checkbox', 'select', 'userstamp', 'textstamp'];

const DetailPanel = ({ preset }) => {
  const dispatch = useDispatch();
  const [local, setLocal] = useState(preset);
  const default_profiles = useSelector(state => state.settings_users?.default_profiles || []);

  // Sync local state with preset
  useEffect(() => {
    setLocal(preset);
  }, [preset]);

  const handleUpdate = async (field, value) => {
    const data = { [field]: value };
    try {
      await dispatch(updateWorkItemPreset(preset.id, data));
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const handleTextChange = (field) => (e) => {
    setLocal(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleTextBlur = (field) => (e) => {
    const value = e.target.value;
    handleUpdate(field, value);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setLocal(prev => ({ ...prev, [field]: value }));
    handleUpdate(field, value);
  };

  const handleProfilesChange = (field) => (e) => {
    const selected = e.target.value;
    const value = selected.includes('all') ? '' : selected.join(',');
    setLocal(prev => ({ ...prev, [field]: value }));
    handleUpdate(field, value);
  };

  const getProfileDisplayName = (id) => {
    const profile = _.find(default_profiles, { id });
    if (!profile) return id;
    return profile.name || id;
  };

  // Reusable Field Row
  const FieldRow = ({ field, label, type = 'text', isBoolean = false }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>{label}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
        {isBoolean ? (
          <FormControlLabel
            control={<Switch checked={!!local[field]} onChange={handleChange(field)} size="small" />}
            label="Default On"
            sx={{ ml: 0 }}
          />
        ) : (
          <TextField
            type={type}
            value={local[field] || (type === 'number' ? 0 : '')}
            onChange={handleTextChange(field)}
            onBlur={handleTextBlur(field)}
            size="small"
            sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
          />
        )}
        <FormControlLabel
          control={<Switch checked={!!local[`${field}_editable`]} onChange={handleChange(`${field}_editable`)} size="small" />}
          label="Editable"
          sx={{ ml: 0 }}
        />
        <TextField
          label="Options"
          value={local[`${field}_options`] || ''}
          onChange={handleTextChange(`${field}_options`)}
          onBlur={handleTextBlur(`${field}_options`)}
          size="small"
          placeholder="opt1,opt2"
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mt: 1 }}>
        <FormControl size="small">
          <InputLabel>Input Type</InputLabel>
          <Select value={local[`${field}_inputtype`] || 'text'} onChange={handleChange(`${field}_inputtype`)}>
            {INPUT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={!!local[`${field}_locked`]} onChange={handleChange(`${field}_locked`)} size="small" />}
          label="Locked"
          sx={{ ml: 0 }}
        />
        <FormControl size="small">
          <InputLabel>Allowed Profiles</InputLabel>
          <Select
            multiple
            value={local[`${field}_allowed_profiles`] ? local[`${field}_allowed_profiles`].split(',') : []}
            onChange={handleProfilesChange(`${field}_allowed_profiles`)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.length === 0 ? (
                  <Chip label="All" size="small" color="default" />
                ) : (
                  selected.map(id => (
                    <Chip
                      key={id}
                      label={getProfileDisplayName(Number(id))}
                      size="small"
                    />
                  ))
                )}
              </Box>
            )}
          >
            <MenuItem value="all"><em>All</em></MenuItem>
            {default_profiles.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {getProfileDisplayName(p.id)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Divider sx={{ my: 2 }} />
    </Box>
  );

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: '#f9f9f9',
        borderRadius: 1,
        maxWidth: 900,
        width: '100%',
        ml: 0,
        mr: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        Edit: {local.name}
      </Typography>

      {/* === BASIC INFO === */}
      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Basic Info</Typography>
      <Divider sx={{ mb: 2 }} />
      <FieldRow field="name" label="Name" />
      <FieldRow field="description" label="Description" />
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Layout Type</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
          <FormControl size="small">
            <InputLabel>Layout Type</InputLabel>
            <Select value={local.layout_type || 'default'} onChange={handleChange('layout_type')}>
              {['default', '4_wheel_input'].map(t => (
                <MenuItem key={t} value={t}>{t === 'default' ? 'Default' : '4 Wheel Input'}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Empty cells to align with FieldRow grid */}
          <Box></Box>
          <Box></Box>
        </Box>
        <Divider sx={{ my: 2 }} />
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Category</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
          <FormControl size="small">
            <InputLabel>Category</InputLabel>
            <Select value={local.category || ''} onChange={handleChange('category')}>
              {['cosmetic', 'mechanical', 'other'].map(c => (
                <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={!!local.category_editable} onChange={handleChange('category_editable')} size="small" />}
            label="Editable"
            sx={{ ml: 0 }}
          />
          <TextField
            label="Options"
            value={local.category_options || ''}
            onChange={handleTextChange('category_options')}
            onBlur={handleTextBlur('category_options')}
            size="small"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mt: 1 }}>
          <FormControl size="small">
            <InputLabel>Input Type</InputLabel>
            <Select value={local.category_inputtype || 'text'} onChange={handleChange('category_inputtype')}>
              {INPUT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={!!local.category_locked} onChange={handleChange('category_locked')} size="small" />}
            label="Locked"
            sx={{ ml: 0 }}
          />
          <FormControl size="small">
            <InputLabel>Allowed Profiles</InputLabel>
            <Select
              multiple
              value={local.category_allowed_profiles ? local.category_allowed_profiles.split(',') : []}
              onChange={handleProfilesChange('category_allowed_profiles')}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.length === 0 ? (
                    <Chip label="All" size="small" color="default" />
                  ) : (
                    selected.map(id => (
                      <Chip
                        key={id}
                        label={getProfileDisplayName(Number(id))}
                        size="small"
                      />
                    ))
                  )}
                </Box>
              )}
            >
              <MenuItem value="all"><em>All</em></MenuItem>
              {default_profiles.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {getProfileDisplayName(p.id)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Divider sx={{ my: 2 }} />
      </Box>

      {/* === APPROVAL FIELDS === */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Approval & Status</Typography>
      <Divider sx={{ mb: 2 }} />
      {['pass', 'recommend', 'required', 'approved', 'declined', 'completed'].map(field => (
        <FieldRow key={field} field={field} label={field.charAt(0).toUpperCase() + field.slice(1)} isBoolean />
      ))}

      {/* === RO STATUS === */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>RO Status</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
          <FormControl size="small">
            <InputLabel>Default Status</InputLabel>
            <Select value={local.ro_status || ''} onChange={handleChange('ro_status')}>
              {['pending', 'approved', 'declined', 'completed'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={!!local.ro_status_editable} onChange={handleChange('ro_status_editable')} size="small" />}
            label="Editable"
            sx={{ ml: 0 }}
          />
          <TextField
            label="Options"
            value={local.ro_status_options || ''}
            onChange={handleTextChange('ro_status_options')}
            onBlur={handleTextBlur('ro_status_options')}
            size="small"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mt: 1 }}>
          <FormControl size="small">
            <InputLabel>Input Type</InputLabel>
            <Select value={local.ro_status_inputtype || 'select'} onChange={handleChange('ro_status_inputtype')}>
              {INPUT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={!!local.ro_status_locked} onChange={handleChange('ro_status_locked')} size="small" />}
            label="Locked"
            sx={{ ml: 0 }}
          />
          <FormControl size="small">
            <InputLabel>Allowed Profiles</InputLabel>
            <Select
              multiple
              value={local.ro_status_allowed_profiles ? local.ro_status_allowed_profiles.split(',') : []}
              onChange={handleProfilesChange('ro_status_allowed_profiles')}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.length === 0 ? (
                    <Chip label="All" size="small" color="default" />
                  ) : (
                    selected.map(id => (
                      <Chip
                        key={id}
                        label={getProfileDisplayName(Number(id))}
                        size="small"
                      />
                    ))
                  )}
                </Box>
              )}
            >
              <MenuItem value="all"><em>All</em></MenuItem>
              {default_profiles.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {getProfileDisplayName(p.id)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Divider sx={{ my: 2 }} />
      </Box>

      {/* === DEFAULT STATUS === */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Default Status</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
          <FormControl size="small">
            <InputLabel>Default</InputLabel>
            <Select value={local.default_status || 'getapproved'} onChange={handleChange('default_status')}>
              {['getapproved', 'approved', 'declined', 'completed'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={!!local.default_status_editable} onChange={handleChange('default_status_editable')} size="small" />}
            label="Editable"
            sx={{ ml: 0 }}
          />
          <TextField
            label="Options"
            value={local.default_status_options || ''}
            onChange={handleTextChange('default_status_options')}
            onBlur={handleTextBlur('default_status_options')}
            size="small"
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mt: 1 }}>
          <FormControl size="small">
            <InputLabel>Input Type</InputLabel>
            <Select value={local.default_status_inputtype || 'select'} onChange={handleChange('default_status_inputtype')}>
              {INPUT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={!!local.default_status_locked} onChange={handleChange('default_status_locked')} size="small" />}
            label="Locked"
            sx={{ ml: 0 }}
          />
          <FormControl size="small">
            <InputLabel>Allowed Profiles</InputLabel>
            <Select
              multiple
              value={local.default_status_allowed_profiles ? local.default_status_allowed_profiles.split(',') : []}
              onChange={handleProfilesChange('default_status_allowed_profiles')}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.length === 0 ? (
                    <Chip label="All" size="small" color="default" />
                  ) : (
                    selected.map(id => (
                      <Chip
                        key={id}
                        label={getProfileDisplayName(Number(id))}
                        size="small"
                      />
                    ))
                  )}
                </Box>
              )}
            >
              <MenuItem value="all"><em>All</em></MenuItem>
              {default_profiles.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {getProfileDisplayName(p.id)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Divider sx={{ my: 2 }} />
      </Box>

      {/* === PRICING FIELDS === */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Pricing & Quantity</Typography>
      <Divider sx={{ mb: 2 }} />
      {[
        { field: 'parts_price', label: 'Parts Price', type: 'number' },
        { field: 'labor_price', label: 'Labor Price', type: 'number' },
        { field: 'labor_units', label: 'Labor Units', type: 'number' },
        { field: 'quantity', label: 'Quantity', type: 'number' },
        { field: 'parts_total', label: 'Parts Total', type: 'number' },
        { field: 'labor_total', label: 'Labor Total', type: 'number' },
        { field: 'estimate_total', label: 'Estimate Total', type: 'number' },
      ].map(({ field, label, type }) => (
        <FieldRow key={field} field={field} label={label} type={type} />
      ))}

      {/* === CUSTOM HEADERS === */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Custom Headers</Typography>
      <Divider sx={{ mb: 2 }} />
      {[1, 2, 3, 4, 5, 6].map(num => (
        <Box key={num} sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2">Custom Header {num}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mt: 1 }}>
            <TextField
              label="Label"
              value={local[`custom_header_${num}`] || ''}
              onChange={handleTextChange(`custom_header_${num}`)}
              onBlur={handleTextBlur(`custom_header_${num}`)}
              size="small"
            />
            <FormControlLabel
              control={<Switch checked={!!local[`custom_header_${num}_editable`]} onChange={handleChange(`custom_header_${num}_editable`)} size="small" />}
              label="Editable"
              sx={{ ml: 0 }}
            />
            <TextField
              label="Options"
              value={local[`custom_header_${num}_options`] || ''}
              onChange={handleTextChange(`custom_header_${num}_options`)}
              onBlur={handleTextBlur(`custom_header_${num}_options`)}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mt: 1 }}>
            <FormControl size="small">
              <InputLabel>Input Type</InputLabel>
              <Select value={local[`custom_header_${num}_inputtype`] || 'text'} onChange={handleChange(`custom_header_${num}_inputtype`)}>
                {INPUT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={!!local[`custom_header_${num}_locked`]} onChange={handleChange(`custom_header_${num}_locked`)} size="small" />}
              label="Locked"
              sx={{ ml: 0 }}
            />
            <FormControl size="small">
              <InputLabel>Allowed Profiles</InputLabel>
              <Select
                multiple
                value={local[`custom_header_${num}_allowed_profiles`] ? local[`custom_header_${num}_allowed_profiles`].split(',') : []}
                onChange={handleProfilesChange(`custom_header_${num}_allowed_profiles`)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <Chip label="All" size="small" color="default" />
                    ) : (
                      selected.map(id => (
                        <Chip
                          key={id}
                          label={getProfileDisplayName(Number(id))}
                          size="small"
                        />
                      ))
                    )}
                  </Box>
                )}
              >
                <MenuItem value="all"><em>All</em></MenuItem>
                {default_profiles.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    {getProfileDisplayName(p.id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      ))}

      {/* === AUTOMOVE === */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Automove</Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
        <TextField label="Counter" type="number" value={local.automove_counter || 0} onChange={handleTextChange('automove_counter')} onBlur={handleTextBlur('automove_counter')} size="small" />
        <FormControlLabel control={<Switch checked={!!local.automove_pending} onChange={handleChange('automove_pending')} size="small" />} label="Pending" />
        <FormControlLabel control={<Switch checked={!!local.automove_getapproved} onChange={handleChange('automove_getapproved')} size="small" />} label="Get Approved" />
        <FormControlLabel control={<Switch checked={!!local.automove_approved} onChange={handleChange('automove_approved')} size="small" />} label="Approved" />
        <FormControlLabel control={<Switch checked={!!local.automove_waiting} onChange={handleChange('automove_waiting')} size="small" />} label="Waiting" />
        <TextField label="Trigger Value" value={local.automove_trigger_value || ''} onChange={handleTextChange('automove_trigger_value')} onBlur={handleTextBlur('automove_trigger_value')} size="small" />
        <FormControlLabel control={<Switch checked={!!local.automove_denied} onChange={handleChange('automove_denied')} size="small" />} label="Denied" />
      </Box>

      {/* === ASSIGNMENT === */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Assignment</Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
        <FormControl size="small">
          <InputLabel>Auto Assign Dept</InputLabel>
          <Select value={local.auto_assign_department_id || ''} onChange={handleChange('auto_assign_department_id')}>
            <MenuItem value="">None</MenuItem>
            {/* departmentOptions passed from parent */}
          </Select>
        </FormControl>
        <FormControl size="small">
          <InputLabel>Assign to User</InputLabel>
          <Select value={local.auto_assign_user || ''} onChange={handleChange('auto_assign_user')}>
            <MenuItem value="">None</MenuItem>
            {/* userOptions passed from parent */}
          </Select>
        </FormControl>
        <TextField label="Trigger" value={local.auto_assign_trigger || ''} onChange={handleTextChange('auto_assign_trigger')} onBlur={handleTextBlur('auto_assign_trigger')} size="small" />
        <TextField label="Source" value={local.assign_to_source || ''} onChange={handleTextChange('assign_to_source')} onBlur={handleTextBlur('assign_to_source')} size="small" />
        <TextField label="Profile Users" value={local.profile_dropdown_users || ''} onChange={handleTextChange('profile_dropdown_users')} onBlur={handleTextBlur('profile_dropdown_users')} size="small" />
      </Box>

      {/* === FLAGS & MISC === */}
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Flags & Misc</Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
        <FormControlLabel control={<Switch checked={!!local.active} onChange={handleChange('active')} size="small" />} label="Active" />
        <FormControlLabel control={<Switch checked={!!local.deleted} onChange={handleChange('deleted')} size="small" />} label="Deleted" />
        <FormControlLabel control={<Switch checked={!!local.superadmin_only} onChange={handleChange('superadmin_only')} size="small" />} label="Superadmin Only" />
        <FormControlLabel control={<Switch checked={!!local.dvi_pdf_show} onChange={handleChange('dvi_pdf_show')} size="small" />} label="PDF Show" />
        <FormControlLabel control={<Switch checked={!!local.mpi_active} onChange={handleChange('mpi_active')} size="small" />} label="MPI Active" />
        <FormControlLabel control={<Switch checked={!!local.group_default} onChange={handleChange('group_default')} size="small" />} label="Group Default" />
        <FormControlLabel control={<Switch checked={!!local.feed_default} onChange={handleChange('feed_default')} size="small" />} label="Feed Default" />
        <TextField label="MPI Order" type="number" value={local.mpi_order || 0} onChange={handleTextChange('mpi_order')} onBlur={handleTextBlur('mpi_order')} size="small" />
        <TextField label="PDF Comments" value={local.dvi_pdf_comments || ''} onChange={handleTextChange('dvi_pdf_comments')} onBlur={handleTextBlur('dvi_pdf_comments')} size="small" multiline rows={1} />
        <TextField label="DVI Desc" value={local.dvi_description || ''} onChange={handleTextChange('dvi_description')} onBlur={handleTextBlur('dvi_description')} size="small" multiline rows={1} />
      </Box>
      <PresetDefaultParts preset={preset} />
    </Box>
  );
};

export default DetailPanel;